import { User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { User, UserRole } from '../types';

// Helper pour ajouter un timeout aux promesses
const withTimeout = <T>(promise: Promise<T>, ms: number, errorMessage: string): Promise<T> => {
  const timeout = new Promise<never>((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), ms);
  });
  return Promise.race([promise, timeout]);
};

export const authService = {
  async login(email: string, password: string): Promise<SupabaseUser> {
    // Timeout de 10 secondes pour le login
    const loginPromise = supabase.auth.signInWithPassword({
      email,
      password
    });

    const { data, error } = await withTimeout(
      loginPromise,
      10000,
      'La connexion prend trop de temps. Vérifiez votre connexion internet.'
    );

    if (error) {
      console.error('Login error:', error);
      if (error.message.includes('Invalid login credentials')) {
        throw new Error('Email ou mot de passe incorrect');
      }
      throw new Error('Échec de la connexion');
    }

    if (!data.user) {
      throw new Error('Échec de la connexion');
    }

    return data.user;
  },

  async logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Logout error:', error);
      throw new Error('Échec de la déconnexion');
    }
  },

  onAuthStateChange(callback: (user: SupabaseUser | null) => void) {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user ?? null;

        callback(user);
      }
    );

    return () => subscription.unsubscribe();
  },

  async getCurrentUser(): Promise<SupabaseUser | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },

  async getSession() {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  },

  async isAuthenticated(): Promise<boolean> {
    const { data } = await supabase.auth.getSession();
    return !!data.session;
  },

  async getUserProfile(userId: string): Promise<User | null> {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      email: data.email,
      role: data.role as UserRole,
      organizationId: data.organization_id,
      displayName: data.display_name,
      avatarUrl: data.avatar_url,
      lastLoginAt: data.last_login_at ? new Date(data.last_login_at) : undefined,
      createdAt: new Date(data.created_at)
    };
  },

  async getCurrentUserProfile(): Promise<User | null> {
    const user = await this.getCurrentUser();
    if (!user) return null;
    return this.getUserProfile(user.id);
  },

  async isSuperAdmin(): Promise<boolean> {
    const profile = await this.getCurrentUserProfile();
    return profile?.role === 'super_admin';
  },

};
